import React, { ChangeEvent, RefObject } from "react";
import { ImagePlus, Trash2, UploadCloud } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Modal } from "../../../components/ui/modal";
import SelectInput from "../../../components/select-input";
import SiteServiceRequestDetailsDrawer from "../../../components/site-service-request-details-drawer";
import { SiteServiceRecordStatus } from "../../../lib/api";

interface ServiceModalsProps {
  isTypeModalOpen: boolean;
  onCloseTypeModal: () => void;
  editingTypeId: string | null;
  typeName: string;
  onTypeNameChange: (value: string) => void;
  typeStatus: SiteServiceRecordStatus;
  onTypeStatusChange: (value: SiteServiceRecordStatus) => void;
  typeIconUrl: string;
  onTypeIconUrlChange: (value: string) => void;
  typeIconPreview: string | null;
  savingType: boolean;
  onSaveType: () => void;
  uploadingIcon: boolean;
  iconInputRef: RefObject<HTMLInputElement | null>;
  onIconSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemoveIcon: () => void;

  deletingTypeId: string | null;
  onCloseDeleteType: () => void;
  deletingType: boolean;
  onDeleteType: () => void;

  selectedRequestId: string | null;
  onCloseRequestDetails: () => void;
}

const ServiceModals: React.FC<ServiceModalsProps> = ({
  isTypeModalOpen,
  onCloseTypeModal,
  editingTypeId,
  typeName,
  onTypeNameChange,
  typeStatus,
  onTypeStatusChange,
  typeIconUrl,
  onTypeIconUrlChange,
  typeIconPreview,
  savingType,
  onSaveType,
  uploadingIcon,
  iconInputRef,
  onIconSelect,
  onRemoveIcon,
  deletingTypeId,
  onCloseDeleteType,
  deletingType,
  onDeleteType,
  selectedRequestId,
  onCloseRequestDetails,
}) => (
  <>
    <Modal
      isOpen={isTypeModalOpen}
      onClose={onCloseTypeModal}
      title={editingTypeId ? "Edit Service Type" : "Add Service Type"}
    >
      <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="space-y-1.5">
          <Label htmlFor="type-name">Name</Label>
          <Input
            id="type-name"
            value={typeName}
            onChange={(e) => onTypeNameChange(e.target.value)}
            placeholder="e.g. Scaffolding training"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Status</Label>
          <SelectInput
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "DISABLED", label: "Disabled" },
            ]}
            value={typeStatus}
            onChange={(v) => onTypeStatusChange(v as SiteServiceRecordStatus)}
          />
        </div>
        {!editingTypeId && (
          <div className="space-y-1.5">
            <Label htmlFor="type-icon-url">Image URL (optional)</Label>
            <Input
              id="type-icon-url"
              value={typeIconUrl}
              onChange={(e) => onTypeIconUrlChange(e.target.value)}
              placeholder="https://example.com/icon.png"
            />
            <p className="text-xs text-gray-500">
              Customers see this image large on mobile when they pick a service. Save first, then edit to upload a photo.
            </p>
          </div>
        )}

        {editingTypeId && (
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <Label>Image</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={uploadingIcon}
                  onClick={() => iconInputRef.current?.click()}
                >
                  <UploadCloud className="h-4 w-4 mr-1.5" />
                  {uploadingIcon ? "Uploading..." : "Upload"}
                </Button>
                {typeIconPreview && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    disabled={uploadingIcon}
                    onClick={() => void onRemoveIcon()}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" />
                    Remove
                  </Button>
                )}
              </div>
              <input
                ref={iconInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                className="hidden"
                onChange={onIconSelect}
              />
            </div>
            {typeIconPreview ? (
              <div className="p-3 rounded-lg border border-gray-100 bg-gray-50">
                <img
                  src={typeIconPreview}
                  alt="Service type"
                  className="h-28 w-full max-w-xs rounded-lg object-cover border border-gray-100"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 border border-dashed border-gray-200 rounded-lg text-gray-400">
                <ImagePlus className="h-8 w-8 mb-2" />
                <p className="text-sm">No image yet</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="type-icon-url-edit">External image URL (optional)</Label>
              <Input
                id="type-icon-url-edit"
                value={typeIconUrl}
                onChange={(e) => onTypeIconUrlChange(e.target.value)}
                placeholder="https://example.com/icon.png"
              />
              <p className="text-xs text-gray-500">
                Set an external URL on save, or use upload for stored icons.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCloseTypeModal}>
            Cancel
          </Button>
          <Button disabled={savingType} onClick={() => void onSaveType()}>
            {savingType ? "Saving..." : editingTypeId ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      isOpen={deletingTypeId !== null}
      onClose={onCloseDeleteType}
      title="Delete service type?"
      description="This fails if requests exist for it. This action cannot be undone."
      className="max-w-md"
    >
      <div className="px-6 pt-5 pb-8 flex justify-end gap-3 flex-wrap shrink-0">
        <Button type="button" variant="outline" onClick={onCloseDeleteType} disabled={deletingType}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => void onDeleteType()}
          disabled={deletingType || !deletingTypeId}
        >
          {deletingType ? "Deleting…" : "Yes, delete service type"}
        </Button>
      </div>
    </Modal>

    <SiteServiceRequestDetailsDrawer
      requestId={selectedRequestId}
      onClose={onCloseRequestDetails}
    />
  </>
);

export default ServiceModals;
